import axios, { AxiosInstance } from 'axios';
import objectHash from 'object-hash';

import { store } from '../../store';
import { acquireAccessToken } from '../auth/authConfig';
import { isBedBoardIntakeForm } from '../helpers';

export const Axios: AxiosInstance = axios.create();

interface Failure {
    api: string;
    count: number;
}

let failures: Failure[] = [];

const cancelledDueToNewRequestMessage = 'Cancelled due to new request';

// A request sent without a token because the renewal failed (or the sign-in redirect is starting) is bound to get a
// 401. That 401 says nothing about the user's access, so it is not retried and does not count towards /noaccess,
// which would take the user off the page and lose an unsaved note.
const sentWithoutToken = new WeakSet<object>();

const markSentWithoutToken = (config: object) => {
    sentWithoutToken.add(config);
};

// Object to store ongoing requests cancel tokens
const pendingRequests = new Map<string, AbortController>();

const getRequestKey = (config): string | null => {
    const dataObj = {
        url: config.url,
        method: config.method,
        data: config.data,
    };

    const isPermissionsRequest = dataObj.url.includes('permissions');
    if (!isPermissionsRequest) {
        // Cancelling the permissions requests if multiple come in cause a longer loading screen
        // Therefore we do not want to cancel these, we can let them process as they come in
        return null;
    }

    // Generate an identifier for each request
    // https://www.npmjs.com/package/object-hash#hashvalue-options
    const requestIdentifier = objectHash(dataObj, {
        ignoreUnknown: true,
    });

    return requestIdentifier;
};

Axios.interceptors.request.use(
    async (config) => {
        const state = store.getState();
        const account = state.auth?.account;

        // Rewrite API URLs to the correct environment at runtime.
        // The app is built once (with Dev URLs baked in) and the same artifact is deployed to QA/Staging/Prod.
        // This interceptor rewrites both medonehp.com URLs and azurewebsites.net dev URLs to the correct
        // environment URL based on the current hostname.
        if (config.url) {
            const hostname = window.location.hostname;

            // Resolve the target environment from the current hostname. The app is built once
            // (Dev URLs baked into the generated client) and the same artifact is deployed to
            // every memryehr.com environment, so baked Dev/legacy URLs are rewritten to the
            // correct env backend at runtime. Env mapping: demo == qa, cert == staging,
            // portal.memryehr.com (bare) == prod.
            const isQA = hostname === 'wonderful-glacier-016ba6210.7.azurestaticapps.net' ||
                hostname === 'proud-tree-026470510.7.azurestaticapps.net' ||
                (hostname.endsWith('.memryehr.com') && (hostname.includes('-qa') || hostname.includes('-demo')));
            const isStaging = hostname === 'purple-sea-0428b8710.7.azurestaticapps.net' ||
                hostname === 'yellow-moss-039802a10.7.azurestaticapps.net' ||
                (hostname.endsWith('.memryehr.com') && (hostname.includes('-staging') || hostname.includes('-cert')));
            const isProd = hostname === 'brave-coast-0c4c63010.7.azurestaticapps.net' ||
                hostname === 'polite-meadow-0794cc510.7.azurestaticapps.net' ||
                hostname === 'portal.memryehr.com' ||
                hostname === 'bedboard-portal.memryehr.com' ||
                (hostname.endsWith('.memryehr.com') && hostname.includes('-prod'));

            const isDevBackendUrl = config.url.includes('mohp-backend-api-dev-centralus.azurewebsites.net') ||
                config.url.includes('mohp-bedboard-api-dev-centralus.azurewebsites.net');
            const isMedonehpUrl = config.url.includes('.medonehp.com');
            const isBedboardUrl = config.url.includes('bedboard');

            // Backend hosts per environment (null target => Dev/local: leave baked URL for the env-var path below)
            let targetApi: string | null = null;
            let targetBedboard: string | null = null;
            if (isQA) {
                targetApi = 'https://mohp-backend-api-qa-centralus.azurewebsites.net';
                targetBedboard = 'https://mohp-bedboard-api-qa-centralus.azurewebsites.net';
            } else if (isStaging) {
                targetApi = 'https://mohp-backend-api-staging-centralus.azurewebsites.net';
                targetBedboard = 'https://mohp-bedboard-api-staging-centralus.azurewebsites.net';
            } else if (isProd) {
                // Production API is fronted by Front Door + WAF (memryehr.com), not the direct App Service host.
                targetApi = 'https://backend-api.memryehr.com';
                targetBedboard = 'https://bedboard-api.memryehr.com';
            }

            if (targetApi && targetBedboard && (isMedonehpUrl || isDevBackendUrl)) {
                const targetUrl = isBedboardUrl ? targetBedboard : targetApi;

                if (isMedonehpUrl) {
                    config.url = config.url.replace(/^https:\/\/[^/]*\.medonehp\.com/, targetUrl);
                } else {
                    config.url = config.url.replace(
                        /^https:\/\/mohp-(?:backend|bedboard)-api-dev-centralus\.azurewebsites\.net/,
                        targetUrl
                    );
                }
            } else if (!targetApi && isMedonehpUrl) {
                // Dev / local: rewrite any baked medonehp.com URL using build-time env vars
                const medonehpApiPattern = /^https:\/\/[^/]*\.medonehp\.com/;
                const bakedIsBedboard = !!config.url.match(/^https:\/\/bedboard-api[^/]*\.medonehp\.com/);
                const apiUrl = process.env.REACT_APP_API_URL ?? '';
                const bedboardApiUrl = process.env.REACT_APP_BEDBOARD_API_URL ?? process.env.REACT_APP_API_URL ?? '';
                const targetUrl = bakedIsBedboard ? bedboardApiUrl : apiUrl;
                if (targetUrl) {
                    config.url = config.url.replace(medonehpApiPattern, targetUrl);
                }
            }
        }

        const url = new URL(config.url);

        if (!isBedBoardIntakeForm() && account && (url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname.endsWith('medonehp.com') || url.hostname.endsWith('memryehr.com') || url.hostname.endsWith('azurewebsites.net'))) {
            try {
                const token = await acquireAccessToken();

                config.headers['Authorization'] = `Bearer ${token}`;

                if (!token) {
                    markSentWithoutToken(config);
                }
            } catch (error) {
                // Log error but continue - the request will fail gracefully if no token
                // The error code only: MSAL's messages can carry details of the account.
                console.error('Failed to acquire access token for request:', (error as { errorCode?: string })?.errorCode ?? 'unknown');

                markSentWithoutToken(config);
            }
        }

        // Generate an identifier for each request
        const requestIdentifier = getRequestKey(config);
        if (requestIdentifier) {
            // Check if there is already a pending request with the same identifier
            if (pendingRequests.has(requestIdentifier)) {
                const sourceController = pendingRequests.get(requestIdentifier);

                // Cancel the previous request
                sourceController.abort(cancelledDueToNewRequestMessage);
            }

            // Create a new CancelToken
            const newController = new AbortController();

            config.signal = newController.signal;

            // Store the new cancel token source in the map
            pendingRequests.set(requestIdentifier, newController);
        }

        return config;
    },

    (error) => {
        if (error.toString().includes(cancelledDueToNewRequestMessage)) {
            return Promise.resolve();
        }

        Promise.reject(error);
    }
);

Axios.interceptors.response.use(
    (response) => {
        const index = failures.findIndex((x) => x.api === response.config.url);

        if (index >= 0) {
            failures.splice(index, 1);
        }

        // Remove completed request from pending map
        const requestIdentifier = getRequestKey(response.config);
        if (requestIdentifier) {
            pendingRequests.delete(requestIdentifier);
        }

        return response;
    },

    async (error) => {
        // Remove failed request from pending map
        if (error?.config) {
            const requestIdentifier = getRequestKey(error.config);
            if (requestIdentifier) {
                pendingRequests.delete(requestIdentifier);
            }
        }

        // Don't show cancel token results here
        if (
            error?.message != null &&
            (error.message.includes('Operation canceled') || error.message.includes('CanceledError') || error.message.includes(cancelledDueToNewRequestMessage))
        ) {
            return Promise.resolve();
        }

        if (error?.config?.url === '/health') {
            return Promise.reject(error);
        }

        if (error?.response != null) {
            if (error.response?.status === 401 && sentWithoutToken.has(error.config)) {
                return Promise.reject(error);
            }

            if (error.response?.status === 401 || error.response?.status === 403) {
                const index = failures.findIndex((x) => x.api === error.config.url);

                if (index >= 0) {
                    if (failures[index].count > 3) {
                        window.location.href = '/noaccess';

                        return Promise.resolve();
                    } else {
                        const newFailures = [...failures];

                        newFailures.splice(index, 1);
                        newFailures.push({ ...failures[index], count: failures[index].count + 1 });

                        failures = newFailures;
                    }
                } else {
                    failures.push({ api: error.config.url, count: 1 });
                }

                try {
                    const token = await acquireAccessToken(true);

                    error.config.headers['Authorization'] = `Bearer ${token}`;

                    return Axios.request(error.config);
                } catch (tokenError) {
                    console.error('Failed to acquire new token after 401/403:', tokenError);

                    return Promise.reject(error);
                }
            }

            // Don't show concurrency errors to the user
            try {
                if (
                    error.response.data != null &&
                    error.response?.status === 500 &&
                    typeof error.response?.data === 'string' &&
                    error.response?.data?.includes('DbUpdateConcurrencyException')
                ) {
                    const isNoteSaveRequest = error?.config?.url?.toLowerCase().includes('api/notes/post');
                    if (isNoteSaveRequest) {
                        return Promise.reject(`This note has changed since your last auto-save. Refresh your note to load the latest version and avoid losing your work.`);
                    }

                    return Promise.reject(`The data you're trying to save has changed since you loaded it. You may have to reload or try again.`);
                }
            } catch {}
        }

        return Promise.reject(error);
    }
);
